import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Eye, MousePointerClick, Pencil, Plus, Trash2 } from "lucide-react";
import { SubPage, Spinner } from "../components/common";
import { ConfirmDialog } from "../components/chat/Dialogs";
import { BannerForm, bannerSrc } from "../components/admin/BannerForm";
import { api, apiError } from "../lib/api";
import { useLang } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";

function BannerRow({ b, first, last, onEdit, onDelete, onMove, onToggle, t }) {
  return (
    <div className="flex items-center gap-3 rounded-2xl bg-white p-3 shadow-[0_2px_12px_rgba(13,42,107,0.07)]" data-testid={`ads-row-${b.id}`}>
      <div className="h-16 w-16 shrink-0 overflow-hidden rounded-xl" style={{ background: b.fit === "contain" ? b.bg || "#000" : "#E8EEF7" }}>
        <img src={bannerSrc(b.image_url)} alt="" className={`h-full w-full ${b.fit === "contain" ? "object-contain" : "object-cover"}`} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate font-bold text-navy" data-testid="ads-row-name">{b.name || "—"}</p>
        <p className="mt-0.5 flex items-center gap-3 text-xs text-slate-500" data-testid="ads-row-stats">
          <span className="inline-flex items-center gap-1"><Eye className="h-3.5 w-3.5" />{b.views}</span>
          <span className="inline-flex items-center gap-1"><MousePointerClick className="h-3.5 w-3.5" />{b.taps}</span>
          {(b.starts_at || b.ends_at) && <span className="truncate">{b.starts_at || "…"} → {b.ends_at || "…"}</span>}
        </p>
        <label className="mt-1 inline-flex items-center gap-1.5 text-xs font-semibold text-navy">
          <input type="checkbox" checked={b.active} onChange={() => onToggle(b)} className="h-4 w-4 accent-[#1E88E5]" data-testid="ads-row-active" />
          {t(b.active ? "ads.on" : "ads.off")}
        </label>
      </div>
      <div className="grid grid-cols-2 gap-1">
        <button onClick={() => onMove(b, "up")} disabled={first} className="rounded-lg p-1.5 text-navy hover:bg-mist disabled:opacity-25" data-testid="ads-row-up"><ArrowUp className="h-4 w-4" /></button>
        <button onClick={() => onEdit(b)} className="rounded-lg p-1.5 text-brand hover:bg-mist" data-testid="ads-row-edit"><Pencil className="h-4 w-4" /></button>
        <button onClick={() => onMove(b, "down")} disabled={last} className="rounded-lg p-1.5 text-navy hover:bg-mist disabled:opacity-25" data-testid="ads-row-down"><ArrowDown className="h-4 w-4" /></button>
        <button onClick={() => onDelete(b)} className="rounded-lg p-1.5 text-alert hover:bg-alert/10" data-testid="ads-row-delete"><Trash2 className="h-4 w-4" /></button>
      </div>
    </div>
  );
}

export default function AdminBannersPage() {
  const { t } = useLang();
  const { user } = useAuth();
  const [list, setList] = useState(null);
  const [editing, setEditing] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const load = () => api.get("/admin/banners").then((r) => setList(r.data)).catch((e) => toast.error(apiError(e, t)));
  useEffect(() => {
    if (user?.is_admin) load();
  }, [user?.is_admin]); // eslint-disable-line react-hooks/exhaustive-deps

  if (user && !user.is_admin) return <Navigate to="/perfil" replace />;
  const act = (fn) => async (...a) => {
    try {
      await fn(...a);
      load();
    } catch (e) {
      toast.error(apiError(e, t));
    }
  };
  const move = act((b, direction) => api.post(`/admin/banners/${b.id}/move`, { direction }));
  const toggle = act((b) => api.put(`/admin/banners/${b.id}`, { ...b, active: !b.active }));
  const remove = act(() => api.delete(`/admin/banners/${toDelete.id}`).then(() => setToDelete(null)));

  return (
    <SubPage title={t("ads.title")} testid="ads-page">
      {editing ? (
        <BannerForm initial={editing.id ? editing : null} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      ) : (
        <>
          <p className="mb-4 text-sm text-slate-500">{t("ads.intro")}</p>
          <button onClick={() => setEditing({})} className="btn-primary mb-4 w-full py-3.5" data-testid="ads-add-btn">
            <Plus className="h-5 w-5" /> {t("ads.add")}
          </button>
          {!list ? <div className="flex justify-center py-10"><Spinner /></div> : list.length === 0 ? (
            <p className="py-8 text-center text-sm text-slate-400" data-testid="ads-empty">{t("ads.empty")}</p>
          ) : (
            <div className="space-y-2.5" data-testid="ads-list">
              {list.map((b, i) => (
                <BannerRow key={b.id} b={b} t={t} first={i === 0} last={i === list.length - 1}
                  onEdit={setEditing} onDelete={setToDelete} onMove={move} onToggle={toggle} />
              ))}
            </div>
          )}
        </>
      )}
      <ConfirmDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)} title={t("ads.deleteTitle")}
        sub={toDelete?.name} confirmLabel={t("ads.delete")} onConfirm={remove} testid="ads-delete-dialog" />
    </SubPage>
  );
}
